import React, { useCallback, useMemo, useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { FileText, MoreHorizontal, ChevronDown, ArrowUpDown, ChevronUp, ChevronDown as ChevronDownMini, Edit2, CalendarClock, BellRing } from "lucide-react";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow
} from "@/components/ui/table";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger
} from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from "@/components/ui/select";
import { TransactionData } from "@/types/types";
import { Skeleton } from "@/components/ui/skeleton";
import { useCurrency } from "@/contexts/CurrencyContext";
import { useIsMobile } from "@/hooks/use-mobile";
import { paidAndLeft } from "@/lib/payments/paymentsView";
import type { CoverageInfo } from "@/types/payments";
import { TransactionDetailsDialog } from "./TransactionDetailsDialog";
import { RemindPaymentDialog } from "./RemindPaymentDialog";
import { MobilePaymentCard } from "./MobilePaymentCard";
import { statusBadgeVariant } from "./paymentBadges";

// Pagination controls (simples)
function clamp(n: number, min: number, max: number) {
  return Math.max(min, Math.min(max, n));
}


interface TransactionListProps {
  transactions: TransactionData[];
  onViewInvoice: (transaction: TransactionData) => void;
  isLoading: boolean;
  coverageBySub?: Record<string, CoverageInfo>;
  fetchPaymentsForSubscription?: (subscriptionId: string) => Promise<TransactionData[]>;
  onUpdatePayment?: (id: string, updates: { status?: string; amount?: number; payment_method?: string }) => Promise<boolean>;
  onDeletePayment?: (id: string) => Promise<boolean>;
  /** Ouvre l'échéancier sur l'abonnement de la ligne (et non sur un montant par défaut). */
  onSplitPayment?: (transaction: TransactionData) => void;
}

type SortKey = "id" | "client" | "amount" | "date" | "status" | "paymentMethod" | "coveragePct" | "paymentsCount" | "amountPaid"| "amountLeft";
type SortDir = "asc" | "desc";

export const TransactionList: React.FC<TransactionListProps> = ({
  transactions,
  onViewInvoice,
  isLoading,
  coverageBySub,
  fetchPaymentsForSubscription,
  onUpdatePayment,
  onDeletePayment,
  onSplitPayment,
}) => {
  const { t } = useTranslation(["payments", "common"]);
  const { formatCurrency } = useCurrency();

  // --- responsive rows per page (6 mobile / 10 desktop) ---
  // useIsMobile (breakpoint 768) plutôt qu'un listener resize local : une
  // seule source JS, alignée sur le basculement table/cartes md:.
  const isMobile = useIsMobile();
  const [rowsPerPage, setRowsPerPage] = useState(isMobile ? 6 : 10);
  useEffect(() => {
    setRowsPerPage(isMobile ? 6 : 10);
  }, [isMobile]);

  // --- sorting state ---
  const [sortKey, setSortKey] = useState<SortKey>("date");
  const [sortDir, setSortDir] = useState<SortDir>("desc");

  const toggleSort = (key: SortKey) => {
    if (sortKey === key) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortKey(key);
      setSortDir("asc");
    }
  };

  // --- accordion cache ---
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});
  const [loadingBySub, setLoadingBySub] = useState<Record<string, boolean>>({});
  const [paymentsCache, setPaymentsCache] = useState<Record<string, TransactionData[]>>({});
  
  // --- editing payment state ---
  const [editingPayment, setEditingPayment] = useState<TransactionData | null>(null);
  const [editingSubId, setEditingSubId] = useState<string | null>(null);

  // --- relance d'impayé ---
  const [remindTx, setRemindTx] = useState<TransactionData | null>(null);

  const toggleExpand = useCallback(
    async (subscriptionId?: string) => {
      if (!subscriptionId) return;
      const isOpen = !!expanded[subscriptionId];

      if (isOpen) {
        setExpanded((e) => ({ ...e, [subscriptionId]: false }));
        return;
      }

      if (!paymentsCache[subscriptionId] && fetchPaymentsForSubscription) {
        try {
          setLoadingBySub((m) => ({ ...m, [subscriptionId]: true }));
          const list = await fetchPaymentsForSubscription(subscriptionId);
          setPaymentsCache((m) => ({ ...m, [subscriptionId]: list }));
        } finally {
          setLoadingBySub((m) => ({ ...m, [subscriptionId]: false }));
        }
      }

      setExpanded((e) => ({ ...e, [subscriptionId]: true }));
    },
    [expanded, paymentsCache, fetchPaymentsForSubscription]
  );

  // --- sorting helper ---
  const sorted = useMemo(() => {
    const arr = [...transactions];
    const getVal = (tx: TransactionData, key: SortKey) => {
      const subId = (tx as any).subscriptionId as string | undefined;
      const cov = subId ? coverageBySub?.[subId] : undefined;
      switch (key) {
        case "id": return String(tx.id ?? "");
        case "client": return (tx.client ?? "").toLowerCase();
        case "amount": return Number(tx.amount ?? 0);
        case "date": return tx.date ? new Date(tx.date).getTime() : 0;
        case "status": return (tx.status ?? "").toLowerCase();
        case "paymentMethod": return (tx.paymentMethod ?? "").toLowerCase();
        case "coveragePct": return cov?.coveragePct ?? -1;
        case "amountPaid": return paidAndLeft(tx, cov).paid;
        case "amountLeft": return paidAndLeft(tx, cov).left;
        case "paymentsCount": return cov?.paymentsCount ?? 0;
      }
    };

    arr.sort((a, b) => {
      const va = getVal(a, sortKey);
      const vb = getVal(b, sortKey);
      if (va < (vb as any)) return sortDir === "asc" ? -1 : 1;
      if (va > (vb as any)) return sortDir === "asc" ? 1 : -1;
      return 0;
    });
    return arr;
  }, [transactions, sortKey, sortDir, coverageBySub]);

  // --- pagination ---
  const [page, setPage] = useState(1);
  useEffect(() => setPage(1), [sorted.length, rowsPerPage]); // reset page if data/page-size changes

  const total = sorted.length || 0;
  const totalPages = Math.max(1, Math.ceil(total / rowsPerPage));
  const curPage = clamp(page, 1, totalPages);
  const startIdx = (curPage - 1) * rowsPerPage;
  const endIdx = Math.min(startIdx + rowsPerPage, total);
  const pageSlice = sorted.slice(startIdx, endIdx);

  // --- headers config (avec responsive + sort icons) ---
  const HeaderCell: React.FC<{ label: string; sort?: SortKey; className?: string }> = ({ label, sort, className }) => {
    const active = sort && sortKey === sort;
    return (
      <TableHead className={`whitespace-nowrap ${className || ""}`}>
        {sort ? (
          <button
            className="flex items-center gap-1 text-xs sm:text-sm font-medium hover:underline"
            onClick={() => toggleSort(sort)}
          >
            {label}
            {active ? (
              sortDir === "asc" ? <ChevronUp className="h-4 w-4" /> : <ChevronDownMini className="h-4 w-4" />
            ) : (
              <ArrowUpDown className="h-4 w-4 opacity-60" />
            )}
          </button>
        ) : (
          <span className="text-xs sm:text-sm font-medium">{label}</span>
        )}
      </TableHead>
    );
  };

  if (isLoading) {
    return (
      <>
        {/* Skeletons lignes (desktop) */}
        <div className="hidden md:block space-y-3">
          {Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className="flex items-center gap-3">
              <Skeleton className="h-4 w-16" />
              <Skeleton className="h-4 w-24" />
              <Skeleton className="h-4 w-20" />
              <Skeleton className="h-4 w-16" />
              <Skeleton className="h-4 w-12" />
            </div>
          ))}
        </div>
        {/* Skeletons cartes (mobile) */}
        <div className="md:hidden space-y-3">
          {Array.from({ length: 4 }).map((_, i) => (
            <Skeleton key={i} className="h-28 rounded-2xl" />
          ))}
        </div>
      </>
    );
  }

  return (
    <div className="rounded-md border">
      <div className="hidden md:block overflow-x-auto"> {/* table desktop uniquement */}
        <Table className="text-xs sm:text-sm"> {/* typo compacte & homogène */}
          <TableHeader>
            <TableRow>
              <HeaderCell label={t("id")} sort="id" className="w-[90px]" />
              <HeaderCell label={t("client")} sort="client" />
              {/* Montant: desktop; en mobile on garde mais format compact */}
              <HeaderCell label={t("amount")} sort="amount" className="w-[90px]" />
              {/* Date: masquée sur très petit si on veut: on garde mais étroite */}
              <HeaderCell label={t("date")} sort="date" className="w-[90px]" />
              {/* Statut: compact */}
              <HeaderCell label={t("status")} sort="status" className="w-[110px]" />
              {/* Méthode: cachée en mobile pour gagner de la place */}
              <HeaderCell label={t("method")} sort="paymentMethod" className="hidden sm:table-cell w-[110px]" />
              {/* Couverture: très compact → % seul sur mobile */}
              <HeaderCell label={t("amountPaid", { defaultValue: "Payé" })} sort="amountPaid" className="w-[90px]" />
              <HeaderCell label={t("amountLeft", { defaultValue: "Restant" })} sort="amountLeft" className="w-[90px]" />

              {/* Nb paiements: triable, bouton accordion: libellé mobile=Voir */}
              <HeaderCell label={t("payments.count", { defaultValue: "Paiements" })} sort="paymentsCount" className="w-[115px]" />
              <TableHead className="w-[70px]">{t("actions")}</TableHead>
            </TableRow>
          </TableHeader>

          <TableBody>
            {pageSlice.length > 0 ? (
              pageSlice.map((tx) => {
                const subId = (tx as any).subscriptionId as string | undefined;
                const cov = subId ? coverageBySub?.[subId] : undefined;
                const paymentsCount = cov?.paymentsCount ?? 0;
                const canExpand = paymentsCount >= 2 && !!subId && !!fetchPaymentsForSubscription;

                const { paid: amountPaid, left: amountLeft } = paidAndLeft(tx, cov);
                // Montant saisi mais pas encore encaissé : affiché à part pour
                // qu'un paiement `pending` ne se lise pas comme un encaissement.
                const pendingAmount = Number(cov?.pendingAmount ?? 0);

                return (
                  <React.Fragment key={tx.id}>
                    <TableRow className="align-middle">
                      <TableCell className="font-medium text-[11px] sm:text-sm whitespace-nowrap">
                        #{tx.id.toString().substring(0, 8)}
                      </TableCell>

                      <TableCell className="whitespace-nowrap">{tx.client || "—"}</TableCell>

                      <TableCell className="whitespace-nowrap">
                        {typeof tx.amount === "number" ? formatCurrency(tx.amount) : tx.amount || "—"}
                      </TableCell>

                      <TableCell className="whitespace-nowrap">{tx.date || "—"}</TableCell>

                      <TableCell className="whitespace-nowrap">
                        <Badge className="text-[10px] sm:text-xs" variant={statusBadgeVariant(tx.status) as any}>
                          {t(`statuses.${(tx.status || "").toLowerCase()}`, { defaultValue: tx.status || "—" })}
                        </Badge>
                      </TableCell>

                      <TableCell className="hidden sm:table-cell whitespace-nowrap">
                        {t(`methods.${tx.paymentMethod}`, { defaultValue: tx.paymentMethod || "—" })}
                      </TableCell>

                      <TableCell className="whitespace-nowrap">
                        {formatCurrency(amountPaid)}
                      </TableCell>
                      <TableCell className="whitespace-nowrap">
                        <div>{formatCurrency(amountLeft)}</div>
                        {pendingAmount > 0 && (
                          <div className="text-[10px] text-muted-foreground">
                            {t("kpi.pending", { defaultValue: "En attente" })}: {formatCurrency(pendingAmount)}
                          </div>
                        )}
                      </TableCell>

                      {/* Paiements + bouton voir/voir les paiements */}
                      <TableCell className="whitespace-nowrap">
                        <div className="flex items-center gap-2">
                          <Badge variant="outline" className="text-[10px] sm:text-xs">{paymentsCount}</Badge>
                          {canExpand && (
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-7 px-2 text-[11px] sm:text-xs"
                              onClick={() => toggleExpand(subId)}
                            >
                              <ChevronDown
                                className={`h-4 w-4 mr-1 transition-transform ${expanded[subId!] ? "rotate-180" : ""}`}
                              />
                              <span className="sm:hidden">{t("payments.showShort", { defaultValue: "Voir" })}</span>
                              <span className="hidden sm:inline">
                                {expanded[subId!]
                                  ? t("payments.hide", { defaultValue: "Masquer" })
                                  : t("payments.show", { defaultValue: "Voir" })}
                              </span>
                            </Button>
                          )}
                        </div>
                      </TableCell>

                      <TableCell className="whitespace-nowrap">
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="sm" className="h-7 w-7 p-0">
                              <span className="sr-only">{t("common:actions")}</span>
                              <MoreHorizontal className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => onViewInvoice(tx)}>
                              <FileText className="mr-2 h-4 w-4" />
                              {t("invoice.preview")}
                            </DropdownMenuItem>
                            {onSplitPayment && subId && (
                              <DropdownMenuItem onClick={() => onSplitPayment(tx)}>
                                <CalendarClock className="mr-2 h-4 w-4" />
                                {t("splitPayment.title")}
                              </DropdownMenuItem>
                            )}
                            {amountLeft > 0 && tx.userId && (
                              <DropdownMenuItem onClick={() => setRemindTx(tx)}>
                                <BellRing className="mr-2 h-4 w-4" />
                                {t("remind.action", { defaultValue: "Relancer" })}
                              </DropdownMenuItem>
                            )}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>

                    {/* Section accordion */}
                    {canExpand && expanded[subId!] && (
                      <TableRow>
                        <TableCell colSpan={10} className="bg-muted/40">
                          <div className="py-1.5 space-y-1.5">
                            {loadingBySub[subId!] ? (
                              <div className="py-1 text-[10px] sm:text-xs text-muted-foreground">
                                {t("common:loading", { defaultValue: "Chargement..." })}
                              </div>
                            ) : (paymentsCache[subId!] || []).length > 0 ? (
                              (paymentsCache[subId!] || []).map((p) => (
                                <div
                                  key={p.id}
                                  className="flex flex-col sm:flex-row sm:items-center sm:justify-between rounded-md border p-1.5 sm:p-2 gap-1 sm:gap-3"
                                >
                                  {/* Bloc gauche (description + date) */}
                                  <div className="flex flex-col leading-tight">
                                    <span className="text-[10px] sm:text-xs font-medium truncate max-w-[160px]">
                                      {p.description || "Payment"}
                                    </span>
                                    <span className="text-[9px] sm:text-[11px] text-muted-foreground">
                                      {p.date} • {t(`methods.${p.paymentMethod}`, { defaultValue: p.paymentMethod })}
                                    </span>
                                  </div>

                                  {/* Bloc droit (badge + montant + bouton edit) */}
                                  <div className="flex items-center justify-between sm:justify-end gap-2 sm:gap-3">
                                    <Badge
                                      className="text-[9px] sm:text-[11px] px-1.5 py-0.5"
                                      variant={statusBadgeVariant(p.status) as any}
                                    >
                                      {t(`statuses.${(p.status || "").toLowerCase()}`, {
                                        defaultValue: p.status || "—",
                                      })}
                                    </Badge>
                                    <span className="text-[10px] sm:text-xs font-semibold whitespace-nowrap">
                                      {typeof p.amount === "number" ? formatCurrency(p.amount) : p.amount}
                                    </span>
                                    {onUpdatePayment && (
                                      <Button
                                        variant="ghost"
                                        size="sm"
                                        className="h-6 w-6 p-0"
                                        onClick={() => {
                                          setEditingPayment(p);
                                          setEditingSubId(subId!);
                                        }}
                                      >
                                        <Edit2 className="h-3 w-3" />
                                      </Button>
                                    )}
                                  </div>
                                </div>
                              ))
                            ) : (
                              <div className="py-1 text-[10px] sm:text-xs text-muted-foreground">
                                {t("common:noData", { defaultValue: "Aucune donnée" })}
                              </div>
                            )}
                          </div>
                        </TableCell>

                      </TableRow>
                    )}
                  </React.Fragment>
                );
              })
            ) : (
              <TableRow>
                <TableCell colSpan={10} className="text-center py-8 text-muted-foreground">
                  {t("common:noData")}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      {/* Vue cartes (mobile < md) : mêmes données, mêmes actions */}
      <div className="md:hidden p-3 space-y-3">
        <div className="flex items-center gap-2">
          <Select value={sortKey} onValueChange={(v) => setSortKey(v as SortKey)}>
            <SelectTrigger className="h-9 flex-1" aria-label={t("sort.label", { defaultValue: "Trier par" })}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="date">{t("date")}</SelectItem>
              <SelectItem value="client">{t("client")}</SelectItem>
              <SelectItem value="amount">{t("amount")}</SelectItem>
              <SelectItem value="amountLeft">{t("amountLeft", { defaultValue: "Restant" })}</SelectItem>
            </SelectContent>
          </Select>
          <Button
            variant="outline"
            size="icon"
            className="h-9 w-9 shrink-0"
            onClick={() => setSortDir((d) => (d === "asc" ? "desc" : "asc"))}
            aria-label={t("sort.toggleDirection", { defaultValue: "Inverser l'ordre" })}
          >
            {sortDir === "asc" ? <ChevronUp className="h-4 w-4" /> : <ChevronDownMini className="h-4 w-4" />}
          </Button>
        </div>

        {pageSlice.length > 0 ? (
          pageSlice.map((tx) => {
            const subId = (tx as any).subscriptionId as string | undefined;
            const cov = subId ? coverageBySub?.[subId] : undefined;
            const paymentsCount = cov?.paymentsCount ?? 0;
            const canExpand = paymentsCount >= 2 && !!subId && !!fetchPaymentsForSubscription;
            return (
              <MobilePaymentCard
                key={tx.id}
                tx={tx}
                cov={cov}
                expanded={!!(subId && expanded[subId])}
                loadingPayments={!!(subId && loadingBySub[subId])}
                payments={(subId && paymentsCache[subId]) || []}
                canExpand={canExpand}
                onToggleExpand={() => toggleExpand(subId)}
                onViewInvoice={() => onViewInvoice(tx)}
                onSplitPayment={onSplitPayment && subId ? () => onSplitPayment(tx) : undefined}
                onRemind={tx.userId ? () => setRemindTx(tx) : undefined}
                onEditPayment={
                  onUpdatePayment && subId
                    ? (p) => {
                        setEditingPayment(p);
                        setEditingSubId(subId);
                      }
                    : undefined
                }
              />
            );
          })
        ) : (
          <div className="rounded-2xl border border-dashed py-10 text-center text-sm text-muted-foreground">
            {t("common:noData")}
          </div>
        )}
      </div>

      {/* Pagination bar */}
      <div className="flex items-center justify-between gap-2 p-2 sm:p-3">
        <div className="text-xs text-muted-foreground">
          {t("common:showing", { defaultValue: "Affichage" })} {total === 0 ? 0 : startIdx + 1}–{endIdx} {t("common:of", { defaultValue: "sur" })} {total}
        </div>
        <div className="flex items-center gap-2">
          <div className="hidden sm:flex items-center gap-2 text-xs">
            <span>{t("common:rowsPerPage", { defaultValue: "Lignes" })}</span>
            <select
              className="border rounded px-2 py-1 text-xs"
              value={rowsPerPage}
              onChange={(e) => setRowsPerPage(Number(e.target.value))}
            >
              {[6, 10, 15, 20].map((n) => (
                <option key={n} value={n}>{n}</option>
              ))}
            </select>
          </div>
          <Button
            variant="outline"
            size="sm"
            className="h-9 px-3"
            onClick={() => setPage((p) => clamp(p - 1, 1, totalPages))}
            disabled={curPage === 1}
          >
            {t("common:prev", { defaultValue: "Préc." })}
          </Button>
          <div className="text-xs min-w-[48px] text-center tabular-nums">
            {curPage}/{totalPages}
          </div>
          <Button
            variant="outline"
            size="sm"
            className="h-9 px-3"
            onClick={() => setPage((p) => clamp(p + 1, 1, totalPages))}
            disabled={curPage === totalPages}
          >
            {t("common:next", { defaultValue: "Suiv." })}
          </Button>
        </div>
      </div>

      {/* Relance d'impayé */}
      {remindTx && (
        <RemindPaymentDialog
          open={!!remindTx}
          onOpenChange={(open) => {
            if (!open) setRemindTx(null);
          }}
          tx={remindTx}
          defaultMessage={t("remind.defaultMessage", {
            client: remindTx.client,
            amount: formatCurrency(
              paidAndLeft(
                remindTx,
                (remindTx as any).subscriptionId
                  ? coverageBySub?.[(remindTx as any).subscriptionId]
                  : undefined
              ).left
            ),
            plan: remindTx.description,
            defaultValue:
              "Bonjour {{client}}, il reste {{amount}} à régler sur votre abonnement {{plan}}. Merci de régulariser votre paiement.",
          })}
        />
      )}

      {/* Dialog d'édition de paiement individuel */}
      {editingPayment && onUpdatePayment && (
        <TransactionDetailsDialog
          open={!!editingPayment}
          onOpenChange={(open) => {
            if (!open) {
              setEditingPayment(null);
              setEditingSubId(null);
            }
          }}
          transaction={editingPayment}
          updateTransaction={async (id, updates) => {
            const success = await onUpdatePayment(id, updates);
            return success;
          }}
          deleteTransaction={onDeletePayment ? async (id) => {
            const success = await onDeletePayment(id);
            return success;
          } : undefined}
          onUpdateSuccess={() => {
            // Invalider le cache pour forcer le rechargement
            if (editingSubId) {
              setPaymentsCache((m) => {
                const copy = { ...m };
                delete copy[editingSubId];
                return copy;
              });
              // Recharger les paiements
              if (fetchPaymentsForSubscription) {
                fetchPaymentsForSubscription(editingSubId).then((list) => {
                  setPaymentsCache((m) => ({ ...m, [editingSubId]: list }));
                });
              }
            }
            setEditingPayment(null);
            setEditingSubId(null);
          }}
          onDeleteSuccess={() => {
            // Invalider le cache pour forcer le rechargement
            if (editingSubId) {
              setPaymentsCache((m) => {
                const copy = { ...m };
                delete copy[editingSubId];
                return copy;
              });
              // Recharger les paiements
              if (fetchPaymentsForSubscription) {
                fetchPaymentsForSubscription(editingSubId).then((list) => {
                  setPaymentsCache((m) => ({ ...m, [editingSubId]: list }));
                });
              }
            }
            setEditingPayment(null);
            setEditingSubId(null);
          }}
        />
      )}
    </div>
  );
};
